// profile.ts — hooks for user profile page sections

import type { LiveViewHook } from "./types";
import type { TerrainInstance, GridCell } from "../terrain-viz";
import { illuminateCssClassProfile } from "../../../.dox/ui";
// Wire validator + type are paradox-generated from `profile.dox`
// (`type GridCell`). JSON.parse on `data-grid` runs through
// validGridCell at the boundary so a malformed payload throws once
// instead of silently rendering a corrupted terrain.
import { validGridCell } from "../../../.dox/profile";

// CSS class names shared with `lib/operation_web/live/profile_live.ex`.
// Pinned through `CssClassProfile` (ui/css-classes.dox).
const PROFILE_AVATAR = illuminateCssClassProfile("profileAvatar");
const PROFILE_NAME = illuminateCssClassProfile("profileName");
const PROFILE_META = illuminateCssClassProfile("profileMeta");
const PROFILE_SOCIALS = illuminateCssClassProfile("profileSocials");

const profileScroller = (): HTMLElement | null =>
  document.querySelector<HTMLElement>(".app-shell > main");

const prefersReducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const revealClass = "profile-motion-visible";
const enterClass = "profile-motion-enter";
const scrollRevealClass = "profile-scroll-reveal";
const scrollRevealedClass = "profile-scroll-revealed";
const observerRootMargin = "0px 0px -12% 0px";

const markVisible = (el: HTMLElement): void => {
  el.classList.add(scrollRevealedClass, revealClass);
};

const animateProfileIdentityIn = (
  target: Element | null,
  delayMs: number,
): void => {
  if (!(target instanceof HTMLElement)) return;
  target.classList.add(enterClass);
  target.style.setProperty("--profile-enter-delay", `${delayMs}ms`);
  requestAnimationFrame(() => {
    target.classList.add(revealClass);
  });
};

const mountScrollReveal = (
  hook: LiveViewHook<never>,
  target: HTMLElement,
  revealChildren?: readonly HTMLElement[],
): void => {
  target.classList.add(scrollRevealClass);
  for (const [idx, child] of (revealChildren ?? []).entries()) {
    child.classList.add(scrollRevealClass);
    child.style.setProperty("--profile-stagger-idx", `${idx}`);
  }

  if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
    markVisible(target);
    for (const child of revealChildren ?? []) markVisible(child);
    return;
  }

  const root = profileScroller();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        markVisible(target);
        for (const child of revealChildren ?? []) markVisible(child);
        observer.disconnect();
        hook["_profileObserver"] = undefined;
        break;
      }
    },
    {
      root,
      rootMargin: observerRootMargin,
      threshold: 0,
    },
  );
  observer.observe(target);
  hook["_profileObserver"] = observer;
};

// -- Avatar glow canvas (pure) -----------------------------------------

const readAccentRgb = (): string => {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--c-accent")
    .trim();
  return raw || "0 112 255";
};

function drawAvatarGlow(canvas: HTMLCanvasElement): void {
  const w = canvas.offsetWidth;
  const h = canvas.offsetHeight;
  if (w === 0 || h === 0) return;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.35;
  const grad = ctx.createRadialGradient(cx, cy, r * 0.7, cx, cy, r * 1.4);
  const accent = readAccentRgb();
  grad.addColorStop(0, `rgb(${accent} / 0.45)`);
  grad.addColorStop(0.5, `rgb(${accent} / 0.18)`);
  grad.addColorStop(1, `rgb(${accent} / 0)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

// -- ProfileIdentity: avatar glow + entrance animation -----------------

export const ProfileIdentity = {
  mounted(this: LiveViewHook<never>): void {
    const canvas = this.el.querySelector<HTMLCanvasElement>(
      "#avatar-glow-canvas",
    );
    if (canvas) drawAvatarGlow(canvas);
    animateProfileIdentityIn(this.el.querySelector(`.${PROFILE_AVATAR}`), 0);
    animateProfileIdentityIn(this.el.querySelector(`.${PROFILE_NAME}`), 100);
    animateProfileIdentityIn(this.el.querySelector(`.${PROFILE_META}`), 200);
    animateProfileIdentityIn(this.el.querySelector(`.${PROFILE_SOCIALS}`), 300);
  },
};

// -- ProfileAbout: scroll-reveal entrance -------------------------------

export const ProfileAbout = {
  mounted(this: LiveViewHook<never>): void {
    mountScrollReveal(this, this.el);
  },
  destroyed(this: LiveViewHook<never>): void {
    const observer = this["_profileObserver"] as
      | IntersectionObserver
      | undefined;
    observer?.disconnect();
  },
};

// -- ProjectStories: scroll-reveal entrance ----------------------------

export const ProjectStories = {
  mounted(this: LiveViewHook<never>): void {
    mountScrollReveal(this, this.el);
  },
  destroyed(this: LiveViewHook<never>): void {
    const observer = this["_profileObserver"] as
      | IntersectionObserver
      | undefined;
    observer?.disconnect();
  },
};

// -- ActivityViz: WebGPU terrain visualization -------------------------

export const ActivityViz = {
  mounted(this: LiveViewHook<never>): void {
    const terrainEl = this.el.querySelector<HTMLElement>("#contrib-terrain");
    if (!terrainEl) return;
    const raw = terrainEl.dataset["grid"];
    // Validate at the JSON.parse boundary — paradox-generated
    // validGridCell throws on missing/wrong fields, so a broken
    // server-side encoder fails loudly rather than silently
    // rendering a corrupted terrain.
    const grid: readonly GridCell[] = raw
      ? (JSON.parse(raw) as unknown[]).map((cell) => validGridCell(cell))
      : [];
    let cancelled = false;
    this["_cancel"] = (): void => {
      cancelled = true;
    };
    void import("../terrain-viz").then(({ create }) => {
      if (cancelled) return;
      create(terrainEl, {})
        .then((instance) => {
          if (cancelled) {
            instance.destroy();
            return;
          }
          this["_terrain"] = instance;
          if (grid.length > 0) instance.update(grid);
        })
        .catch(() => {
          if (cancelled) return;
          const fallback =
            this.el.querySelector<HTMLElement>("[data-fallback]");
          if (fallback) {
            fallback.style.display = "";
          }
          terrainEl.style.display = "none";
        });
    });
  },
  updated(this: LiveViewHook<never>): void {
    const terrainEl = this.el.querySelector<HTMLElement>("#contrib-terrain");
    if (!terrainEl) return;
    const raw = terrainEl.dataset["grid"];
    const grid: readonly GridCell[] = raw
      ? (JSON.parse(raw) as unknown[]).map((cell) => validGridCell(cell))
      : [];
    const t = this["_terrain"] as TerrainInstance | undefined;
    if (t && grid.length > 0) t.update(grid);
  },
  destroyed(this: LiveViewHook<never>): void {
    const cancel = this["_cancel"] as (() => void) | undefined;
    if (cancel) cancel();
    const t = this["_terrain"] as TerrainInstance | undefined;
    if (t) t.destroy();
  },
};

// -- Engagement: staggered scroll-reveal for metric cards --------------

export const Engagement = {
  mounted(this: LiveViewHook<never>): void {
    const cards = Array.from(
      this.el.querySelectorAll<HTMLElement>(".engagement-card"),
    );
    if (cards.length === 0) return;
    mountScrollReveal(this, this.el, cards);
  },
  destroyed(this: LiveViewHook<never>): void {
    const observer = this["_profileObserver"] as
      | IntersectionObserver
      | undefined;
    observer?.disconnect();
  },
};

// -- ContribTerrain: stub (element uses phx-update="ignore", no phx-hook) --

export const ContribTerrain = {
  mounted(this: LiveViewHook<never>): void {},
};
